import { apiRoutes, version } from "@sr-partner-portal/common";
import type { RootOptions } from "react-dom/client";

// Sends browser errors to the API, which writes them to its log. Bugsnag only
// reports production, and nobody watching the server logs sees it; this way
// errors from staging and production show up where the API errors are.

const MAX_REPORTS_PER_PAGE_LOAD = 10;

// Noise that says nothing about our code.
const IGNORED_MESSAGES = [/ResizeObserver loop/, /^Script error\.?$/];

const reportedKeys = new Set<string>();

const reportClientError = (app: string, kind: string, error: unknown) => {
    try {
        const message =
            error instanceof Error ? error.message : String(error ?? "");
        const stack = error instanceof Error ? error.stack : undefined;

        if (IGNORED_MESSAGES.some((pattern) => pattern.test(message))) {
            return;
        }

        const key = `${kind}:${message}`;
        if (
            reportedKeys.has(key) ||
            reportedKeys.size >= MAX_REPORTS_PER_PAGE_LOAD
        ) {
            return;
        }
        reportedKeys.add(key);

        void fetch(apiRoutes().clientErrors().build(), {
            method: "POST",
            keepalive: true,
            headers: { "content-type": "application/json" },
            body: JSON.stringify({
                app,
                kind,
                version,
                message,
                stack,
                url: window.location.href,
            }),
        }).catch(() => {});
    } catch {
        // Reporting must never break the app.
    }
};

const installClientErrorReporting = (app: string) => {
    window.addEventListener("error", (event) => {
        reportClientError(app, "error", event.error ?? event.message);
    });
    window.addEventListener("unhandledrejection", (event) => {
        reportClientError(app, "unhandledrejection", event.reason);
    });
};

// React 19 hands render errors to these root callbacks instead of the window.
// The default handlers log to the console, so ours do too.
const getClientErrorRootOptions = (app: string): RootOptions => ({
    onCaughtError: (error, errorInfo) => {
        console.error(error, errorInfo.componentStack);
        reportClientError(app, "react-caught", error);
    },
    onUncaughtError: (error, errorInfo) => {
        console.error(error, errorInfo.componentStack);
        reportClientError(app, "react-uncaught", error);
    },
});

export { getClientErrorRootOptions, installClientErrorReporting };
